export type ExportRelativePreset =
  | "current_month"
  | "last_month"
  | "current_quarter"
  | "last_quarter"
  | "current_year"
  | "last_year";

export type ExportQuarter = 1 | 2 | 3 | 4;

/** What the admin picked. Only the resolved calendar dates are sent to the API. */
export type ExportPeriod =
  | { kind: "relative"; preset: ExportRelativePreset }
  | { kind: "month"; year: number; month: number }
  | { kind: "quarter"; year: number; quarter: ExportQuarter }
  | { kind: "year"; year: number }
  | { kind: "custom"; from: string; to: string };

export interface ExportDateRange {
  dateFrom: string;
  dateTo: string;
  dateToExclusive: string;
  label: string;
  isPartial: boolean;
}

export const DEFAULT_EXPORT_PERIOD: ExportPeriod = {
  kind: "relative",
  preset: "last_month",
};

export const EXPORT_RELATIVE_PRESETS: {
  preset: ExportRelativePreset;
  label: string;
}[] = [
  { preset: "current_month", label: "Dieser Monat" },
  { preset: "last_month", label: "Letzter Monat" },
  { preset: "current_quarter", label: "Dieses Quartal" },
  { preset: "last_quarter", label: "Letztes Quartal" },
  { preset: "current_year", label: "Dieses Jahr" },
  { preset: "last_year", label: "Letztes Jahr" },
];

export const EXPORT_MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
] as const;

const MIN_DATE = "1900-01-01";

type CalendarDate = { year: number; month: number; day: number };

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function parseCalendarDate(value: string): CalendarDate {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match)
    throw new RangeError("Ungültiges Datum. Bitte JJJJ-MM-TT verwenden.");

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth(year, month)
  ) {
    throw new RangeError("Ungültiges Kalenderdatum.");
  }
  return { year, month, day };
}

function calendarDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(
    2,
    "0"
  )}-${String(day).padStart(2, "0")}`;
}

function nextDay(value: string): string {
  const { year, month, day } = parseCalendarDate(value);
  if (day < daysInMonth(year, month)) return calendarDate(year, month, day + 1);
  if (month < 12) return calendarDate(year, month + 1, 1);
  return calendarDate(year + 1, 1, 1);
}

/** The current calendar date in Berlin, independent of the browser's timezone. */
export function getBerlinToday(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const part = (type: string) =>
    parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function formatExportDate(value: string): string {
  const { year, month, day } = parseCalendarDate(value);
  return `${String(day).padStart(2, "0")}.${String(month).padStart(
    2,
    "0"
  )}.${String(year).padStart(4, "0")}`;
}

export function isMonthAvailable(
  year: number,
  month: number,
  today: string = getBerlinToday()
): boolean {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    year < 1900 ||
    month < 1 ||
    month > 12
  ) {
    return false;
  }
  parseCalendarDate(today);
  return calendarDate(year, month, 1) <= today;
}

export function isQuarterAvailable(
  quarter: ExportQuarter,
  year: number,
  today: string = getBerlinToday()
): boolean {
  if (![1, 2, 3, 4].includes(quarter)) return false;
  return isMonthAvailable(year, (quarter - 1) * 3 + 1, today);
}

function quarterOf(month: number): ExportQuarter {
  return (Math.floor((month - 1) / 3) + 1) as ExportQuarter;
}

/**
 * Inclusive span [first, last] clipped to today. The caller has already
 * checked that `first` is not in the future.
 */
function clipped(
  first: string,
  last: string,
  label: string,
  today: string
): ExportDateRange {
  const isPartial = today < last;
  const dateTo = isPartial ? today : last;
  return {
    dateFrom: first,
    dateTo,
    dateToExclusive: nextDay(dateTo),
    label: `${label}${isPartial ? " bis heute" : ""}`,
    isPartial,
  };
}

function monthRange(year: number, month: number, today: string) {
  if (!isMonthAvailable(year, month, today)) {
    throw new RangeError("Dieser Monat liegt in der Zukunft.");
  }
  return clipped(
    calendarDate(year, month, 1),
    calendarDate(year, month, daysInMonth(year, month)),
    `${EXPORT_MONTH_NAMES[month - 1]} ${year}`,
    today
  );
}

function quarterRange(year: number, quarter: ExportQuarter, today: string) {
  if (!isQuarterAvailable(quarter, year, today)) {
    throw new RangeError("Dieses Quartal liegt in der Zukunft.");
  }
  const startMonth = (quarter - 1) * 3 + 1;
  return clipped(
    calendarDate(year, startMonth, 1),
    calendarDate(year, startMonth + 2, daysInMonth(year, startMonth + 2)),
    `Q${quarter} ${year}`,
    today
  );
}

function yearRange(year: number, today: string) {
  if (!isMonthAvailable(year, 1, today)) {
    throw new RangeError(
      "Das ausgewählte Jahr darf nicht in der Zukunft liegen."
    );
  }
  return clipped(
    calendarDate(year, 1, 1),
    calendarDate(year, 12, 31),
    `Ganzes Jahr ${year}`,
    today
  );
}

/** Resolve inclusive UI dates and the exclusive database upper bound as calendar dates. */
export function resolveExportPeriod(
  period: ExportPeriod,
  today: string = getBerlinToday()
): ExportDateRange {
  const now = parseCalendarDate(today);
  switch (period.kind) {
    case "month":
      return monthRange(period.year, period.month, today);
    case "quarter":
      return quarterRange(period.year, period.quarter, today);
    case "year":
      return yearRange(period.year, today);
    case "custom": {
      const { from, to } = period;
      if (!from || !to) {
        throw new RangeError("Bitte Start- und Enddatum angeben.");
      }
      parseCalendarDate(from);
      parseCalendarDate(to);
      if (from < MIN_DATE) {
        throw new RangeError("Das Startdatum muss ab dem 01.01.1900 liegen.");
      }
      if (from > to) {
        throw new RangeError(
          "Das Startdatum darf nicht nach dem Enddatum liegen."
        );
      }
      if (to > today) {
        throw new RangeError("Das Enddatum darf nicht in der Zukunft liegen.");
      }
      return {
        dateFrom: from,
        dateTo: to,
        dateToExclusive: nextDay(to),
        label: `${formatExportDate(from)}–${formatExportDate(to)}`,
        isPartial: false,
      };
    }
    case "relative": {
      const quarter = quarterOf(now.month);
      const presetLabel = EXPORT_RELATIVE_PRESETS.find(
        (item) => item.preset === period.preset
      )?.label;
      const withLabel = (range: ExportDateRange) => ({
        ...range,
        label: `${presetLabel}${range.isPartial ? " bis heute" : ""}`,
      });
      switch (period.preset) {
        case "current_month":
          return withLabel(monthRange(now.year, now.month, today));
        case "last_month":
          return withLabel(
            now.month === 1
              ? monthRange(now.year - 1, 12, today)
              : monthRange(now.year, now.month - 1, today)
          );
        case "current_quarter":
          return withLabel(quarterRange(now.year, quarter, today));
        case "last_quarter":
          return withLabel(
            quarter === 1
              ? quarterRange(now.year - 1, 4, today)
              : quarterRange(now.year, (quarter - 1) as ExportQuarter, today)
          );
        case "current_year":
          return withLabel(yearRange(now.year, today));
        case "last_year":
          return withLabel(yearRange(now.year - 1, today));
      }
    }
  }
  throw new RangeError("Unbekannter Zeitraum.");
}
